import type { Connectivity, Display, Health, PlayerSummary, PlayerTelemetry } from '@/types/api'

/**
 * Derived display status for the big pill and status dots.
 *
 * Lifecycle (ARCHITECTURE §7) wins over connectivity: a display without an active player is
 * PENDING (never had one) or ORPHANED (player retired, awaiting replacement). Active displays
 * show their connectivity; health `warning`/`error` is an overlay (amber/rose) on top.
 */
export type DisplayPillKind = 'online' | 'offline' | 'unknown' | 'pending' | 'orphaned' | 'archived'
export type StatusTone = 'success' | 'offline' | 'muted' | 'info' | 'warning' | 'danger'

export interface DisplayPill {
  kind: DisplayPillKind
  label: string
  /** Base tone before overlay. */
  tone: StatusTone
  /** Health overlay, only meaningful while the display has a player. */
  overlay: 'warning' | 'error' | null
  /** Tone after applying the overlay — what dots/pills render. */
  effectiveTone: StatusTone
  description: string
}

const PILL_COPY: Record<DisplayPillKind, { label: string; tone: StatusTone; description: string }> = {
  online: { label: 'Online', tone: 'success', description: 'The player is sending heartbeats.' },
  offline: { label: 'Offline', tone: 'offline', description: 'No heartbeat for more than 3 heartbeat intervals.' },
  unknown: { label: 'Unknown', tone: 'muted', description: 'Waiting for the first heartbeat.' },
  pending: { label: 'Pending', tone: 'info', description: 'No player paired yet.' },
  orphaned: { label: 'Orphaned', tone: 'warning', description: 'The player was retired. Pair a replacement.' },
  archived: { label: 'Archived', tone: 'muted', description: 'This display is archived.' },
}

export function deriveDisplayPill(display: Pick<Display, 'status' | 'connectivity' | 'health'> & { player?: unknown }): DisplayPill {
  let kind: DisplayPillKind
  if (display.status === 'archived') kind = 'archived'
  else if (display.status === 'pending') kind = 'pending'
  else if (display.status === 'orphaned') kind = 'orphaned'
  else kind = display.connectivity

  const hasPlayer = kind === 'online' || kind === 'offline' || kind === 'unknown'
  const overlay = hasPlayer ? healthOverlay(display.health) : null
  const copy = PILL_COPY[kind]
  // Offline/unknown keep their neutral tone: stale health shouldn't look "live".
  const effectiveTone: StatusTone = overlay && kind === 'online' ? (overlay === 'error' ? 'danger' : 'warning') : copy.tone
  return { kind, label: copy.label, tone: copy.tone, overlay, effectiveTone, description: copy.description }
}

export function healthOverlay(health: Health | null | undefined): 'warning' | 'error' | null {
  return health === 'warning' || health === 'error' ? health : null
}

export function connectivityTone(connectivity: Connectivity): StatusTone {
  return connectivity === 'online' ? 'success' : connectivity === 'offline' ? 'offline' : 'muted'
}

export const CONNECTIVITY_LABELS: Record<Connectivity, string> = { online: 'Online', offline: 'Offline', unknown: 'Unknown' }

/** Tailwind classes per tone (dot/fill, soft background, text). */
export const TONE_CLASSES: Record<StatusTone, { dot: string; soft: string; text: string; ring: string }> = {
  success: { dot: 'bg-success', soft: 'bg-success/12', text: 'text-[color-mix(in_oklch,var(--success),black_25%)] dark:text-success', ring: 'ring-success/30' },
  offline: { dot: 'bg-offline', soft: 'bg-offline/15', text: 'text-muted-foreground', ring: 'ring-offline/30' },
  muted: { dot: 'bg-muted-foreground/40', soft: 'bg-muted', text: 'text-muted-foreground', ring: 'ring-border' },
  info: { dot: 'bg-info', soft: 'bg-info/12', text: 'text-[color-mix(in_oklch,var(--info),black_25%)] dark:text-info', ring: 'ring-info/30' },
  warning: { dot: 'bg-warning', soft: 'bg-warning/15', text: 'text-[color-mix(in_oklch,var(--warning),black_40%)] dark:text-warning', ring: 'ring-warning/40' },
  danger: { dot: 'bg-danger', soft: 'bg-danger/12', text: 'text-danger', ring: 'ring-danger/30' },
}

/* ------------------------------------------------------- player runtime */

/** Derived player runtime status (PLAYER_PROTOCOL.md §10.3). */
export type PlayerRuntimeKind =
  | 'offline'
  | 'updating'
  | 'synchronizing'
  | 'downloading'
  | 'playing'
  | 'online'
  | 'disabled'
  | 'retired'
  | 'provisioning'
  | 'unknown'

export interface PlayerRuntime {
  kind: PlayerRuntimeKind
  label: string
  overlay: 'warning' | 'error' | null
}

const RUNTIME_LABELS: Record<PlayerRuntimeKind, string> = {
  offline: 'Offline',
  updating: 'Updating',
  synchronizing: 'Synchronizing',
  downloading: 'Downloading',
  playing: 'Playing',
  online: 'Online',
  disabled: 'Disabled',
  retired: 'Retired',
  provisioning: 'Provisioning',
  unknown: 'Unknown',
}

export function derivePlayerRuntime(
  player: Pick<PlayerSummary, 'status' | 'runtime_state'> | null | undefined,
  connectivity: Connectivity,
  telemetry?: Pick<PlayerTelemetry, 'state' | 'health'> | null,
): PlayerRuntime {
  const make = (kind: PlayerRuntimeKind, overlay: PlayerRuntime['overlay'] = null): PlayerRuntime => ({ kind, label: RUNTIME_LABELS[kind], overlay })
  if (!player) return make('unknown')
  if (player.status === 'disabled' || player.status === 'retired' || player.status === 'provisioning') return make(player.status)
  if (connectivity === 'offline') return make('offline')
  const state = telemetry?.state ?? player.runtime_state ?? null
  const overlay: PlayerRuntime['overlay'] = state === 'safe_mode' || state === 'error' ? 'error' : healthOverlay(telemetry?.health)
  switch (state) {
    case 'updating':
      return make('updating', overlay)
    case 'synchronizing':
      return make('synchronizing', overlay)
    case 'downloading':
      return make('downloading', overlay)
    case 'playing':
      return make('playing', overlay)
    default:
      return make(connectivity === 'online' ? 'online' : 'unknown', overlay)
  }
}

/* ---------------------------------------------------------------- misc */

export function hasWarnings(display: Pick<Display, 'warnings' | 'health'>): boolean {
  return display.warnings.length > 0 || display.health === 'warning' || display.health === 'error'
}

export function formatUptime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) return '—'
  const d = Math.floor(seconds / 86_400)
  const h = Math.floor((seconds % 86_400) / 3_600)
  const m = Math.floor((seconds % 3_600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  return `${Math.max(m, 0)}m`
}

export function formatSkew(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—'
  const abs = Math.abs(ms)
  const sign = ms > 0 ? '+' : ms < 0 ? '−' : '±'
  if (abs < 1000) return `${sign}${abs} ms`
  return `${sign}${(abs / 1000).toFixed(abs < 10_000 ? 1 : 0)} s`
}

/** Clock skew above 2 s is worth flagging (PLAYER_PROTOCOL.md §18 uses multi-second thresholds). */
export function skewTone(ms: number | null | undefined): 'ok' | 'warning' | 'critical' {
  if (ms === null || ms === undefined) return 'ok'
  const abs = Math.abs(ms)
  return abs >= 30_000 ? 'critical' : abs >= 2_000 ? 'warning' : 'ok'
}

export function shortId(id: string | null | undefined, length = 12): string {
  if (!id) return '—'
  const raw = id.startsWith('sha256:') ? id.slice(7) : id
  return raw.length > length ? `${raw.slice(0, length)}…` : raw
}

export const ORIENTATION_LABELS: Record<Display['orientation'], string> = {
  landscape: 'Landscape',
  portrait: 'Portrait',
  landscape_flipped: 'Landscape (flipped)',
  portrait_flipped: 'Portrait (flipped)',
}

export const PLATFORM_LABELS: Record<PlayerSummary['platform'], string> = {
  linux: 'Linux',
  raspberry_pi: 'Raspberry Pi',
  windows: 'Windows',
  android_tv: 'Android TV',
  tizen: 'Samsung Tizen',
  webos: 'LG webOS',
}

export function platformLabel(platform: string | null | undefined): string {
  if (!platform) return 'Unknown'
  return (PLATFORM_LABELS as Record<string, string>)[platform] ?? platform.replace(/_/g, ' ')
}

/* ---------------------------------------------------------------- sync */

export interface SyncStateInfo {
  label: string
  tone: StatusTone
}

export function deriveSyncState(display: Pick<Display, 'sync' | 'player'>): SyncStateInfo {
  if (!display.player) return { label: 'No player', tone: 'muted' }
  const s = display.sync
  if (s.last_sync_state === 'failed') return { label: 'Sync failed', tone: 'danger' }
  if (s.last_sync_state === 'rolled_back') return { label: 'Rolled back', tone: 'warning' }
  if (s.in_sync) return { label: 'In sync', tone: 'success' }
  if (!s.last_sync_at) return { label: 'Never synced', tone: 'muted' }
  return { label: 'Out of sync', tone: 'info' }
}
